import { useState, useRef, useEffect } from "react";
import {
  X,
  Search,
  Link2,
  Check,
  ShieldCheck,
  UserPlus,
  MessageSquare,
  Copy,
  RotateCcw,
  FileCheck2,
} from "lucide-react";

import Navbar from "../DoxzonNew/Navbar";

/* ── Doxzon palette (matches Hero.jsx tokens) ───────────────────────── */
const C = {
  navy: "#1e4d6b",
  navyDark: "#0d2d45",
  teal: "#0ba88a",
  tealLight: "#3dd6c8",
  accent: "#12c4b4",
  // "Create ABHA" now uses the same navy/teal blue as the Save Patient gradient
  orange: "#0d2d45",
  orangeDark: "#0a2236",
  orangeBg: "#e3edf1",
  green: "#16a34a",
  greenBg: "#eafcf2",
  ink: "#1e3a5f",
  sub: "#7c8ba1",
  border: "#e4e9ee",
  fieldBg: "#f7f9fb",
  page: "#eef2f6",
  card: "#ffffff",
  // primary CTA / banner gradient — matches the "Connect with us" reference
  grad: "linear-gradient(135deg, #0d2d45 0%, #0ba88a 55%, #3dd6c8 100%)",
};

/* ── Top progress stepper ───────────────────────────────────────────── */
const STEPS = [
  "Patient Registration",
  "ABHA Search",
  "Generate OTP",
  "Verify OTP",
  "ABHA Details",
  "Link Success",
];
const SUBLABELS = [
  "Enter patient details",
  "Search existing ABHA",
  "OTP sent to patient",
  "Verify OTP",
  "Verify & link ABHA",
  "ABHA linked",
];

function Stepper({ current }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: "12px",
        padding: "16px 24px",
        marginBottom: "20px",
        overflowX: "auto",
        gap: 0,
      }}
    >
      {STEPS.map((label, i) => {
        const idx = i + 1;
        const done = idx < current;
        const active = idx === current;
        return (
          <div
            key={label}
            style={{
              display: "flex",
              alignItems: "center",
              flex: i === STEPS.length - 1 ? "0 0 auto" : "1 1 0",
              minWidth: "128px",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span
                  style={{
                    width: "22px",
                    height: "22px",
                    borderRadius: "9999px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "11px",
                    fontWeight: 700,
                    background: done ? C.green : active ? C.grad : "#eef1f4",
                    color: done || active ? "#fff" : "#9aa5b3",
                  }}
                >
                  {done ? <Check size={12} strokeWidth={3} /> : idx}
                </span>
                <span
                  style={{
                    fontSize: "12.5px",
                    fontWeight: 600,
                    color: done ? C.green : active ? C.teal : "#9aa5b3",
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </span>
              </div>
              <span style={{ fontSize: "10.5px", color: C.sub, paddingLeft: "30px", whiteSpace: "nowrap" }}>
                {SUBLABELS[i]}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                style={{
                  flex: 1,
                  height: "2px",
                  background: idx < current ? C.green : "#e4e9ee",
                  margin: "0 12px",
                  minWidth: "24px",
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Reusable modal shell ────────────────────────────────────────────── */
function Modal({ children, onClose, width = 420 }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(13,29,60,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "20px",
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        style={{
          width: `${width}px`,
          maxWidth: "100%",
          background: C.card,
          borderRadius: "14px",
          boxShadow: "0 24px 64px rgba(10,30,50,0.28)",
          padding: "22px 24px 24px",
          animation: "abhaPop .22s cubic-bezier(.34,1.56,.64,1)",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ badge, badgeBg, badgeColor, title, subtitle, onClose }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "18px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <span
          style={{
            width: "24px",
            height: "24px",
            borderRadius: "9999px",
            background: badgeBg,
            color: badgeColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "12px",
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {badge}
        </span>
        <div>
          <p style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: C.ink }}>{title}</p>
          {subtitle && <p style={{ margin: 0, fontSize: "11.5px", color: C.sub, marginTop: "1px" }}>{subtitle}</p>}
        </div>
      </div>
      <button
        onClick={onClose}
        style={{ border: "none", background: "transparent", cursor: "pointer", color: "#9aa5b3", padding: "2px" }}
      >
        <X size={16} />
      </button>
    </div>
  );
}

function ModalButtons({ leftLabel, onLeft, rightLabel, onRight, rightBg, rightDisabled, rightIcon }) {
  return (
    <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
      <button
        onClick={onLeft}
        style={{
          flex: 1,
          padding: "10px 0",
          borderRadius: "9px",
          border: `1px solid ${C.border}`,
          background: "#fff",
          color: C.ink,
          fontSize: "13px",
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        {leftLabel}
      </button>
      <button
        onClick={onRight}
        disabled={rightDisabled}
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
          padding: "10px 0",
          borderRadius: "9px",
          border: "none",
          background: rightDisabled ? "#c9d2db" : rightBg,
          color: "#fff",
          fontSize: "13px",
          fontWeight: 600,
          cursor: rightDisabled ? "default" : "pointer",
        }}
      >
        {rightIcon}
        {rightLabel}
      </button>
    </div>
  );
}

/* ── 6-digit OTP input ───────────────────────────────────────────────── */
function OtpBoxes({ value, onChange, accent }) {
  const refs = useRef([]);
  const digits = value.split("");
  while (digits.length < 6) digits.push("");

  function handleChange(i, v) {
    const d = v.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = d;
    onChange(next.join(""));
    if (d && refs.current[i + 1]) refs.current[i + 1].focus();
  }
  function handleKeyDown(i, e) {
    if (e.key === "Backspace" && !digits[i] && refs.current[i - 1]) {
      refs.current[i - 1].focus();
    }
  }

  return (
    <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          value={d}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          inputMode="numeric"
          maxLength={1}
          style={{
            width: "42px",
            height: "46px",
            textAlign: "center",
            fontSize: "17px",
            fontWeight: 700,
            color: C.ink,
            borderRadius: "9px",
            border: `1.5px solid ${d ? accent : C.border}`,
            outline: "none",
          }}
        />
      ))}
    </div>
  );
}

/* ── ABHA card (right-hand result panel) ────────────────────────────── */
function AbhaCard({ data }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard?.writeText(data.number);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <div style={{ borderRadius: "16px", overflow: "hidden", border: `1px solid ${C.border}`, boxShadow: "0 8px 24px rgba(10,40,60,0.08)" }}>
      <div style={{ background: C.grad, padding: "16px 18px", display: "flex", alignItems: "center", gap: "10px" }}>
        <ShieldCheck size={20} color="#fff" />
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: "10.5px", color: "rgba(255,255,255,0.7)" }}>ABHA CARD</p>
          <p style={{ margin: 0, fontSize: "13px", fontWeight: 700, color: "#fff" }}>Ayushman Bharat Health Account</p>
        </div>
        <span style={{ display: "flex", alignItems: "center", gap: "4px", background: "rgba(255,255,255,0.15)", color: "#8ff0c8", borderRadius: "9999px", padding: "3px 9px", fontSize: "10px", fontWeight: 700 }}>
          <Check size={11} strokeWidth={3} /> Linked
        </span>
      </div>

      <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.border}`, background: C.greenBg }}>
        <p style={{ margin: 0, fontSize: "10px", fontWeight: 700, color: "#0f8a4c", letterSpacing: "0.03em" }}>ABHA NUMBER</p>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <p style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: C.ink }}>{data.number}</p>
          <button onClick={copy} style={{ border: "none", background: "rgba(15,138,76,0.1)", borderRadius: "6px", padding: "5px", cursor: "pointer", color: "#0f8a4c" }}>
            <Copy size={13} />
          </button>
        </div>
        {copied && <p style={{ margin: 0, fontSize: "10px", color: "#0f8a4c" }}>Copied!</p>}
      </div>

      <div style={{ padding: "12px 18px", borderBottom: `1px solid ${C.border}` }}>
        <p style={{ margin: 0, fontSize: "10px", fontWeight: 700, color: C.sub, letterSpacing: "0.03em" }}>ABHA ADDRESS</p>
        <p style={{ margin: 0, fontSize: "13px", fontWeight: 600, color: C.ink }}>{data.address}</p>
      </div>

      <div style={{ padding: "14px 18px" }}>
        <p style={{ margin: 0, fontSize: "10px", fontWeight: 700, color: C.sub, letterSpacing: "0.03em", marginBottom: "6px" }}>PATIENT DETAILS</p>
        <p style={{ margin: 0, fontSize: "13.5px", fontWeight: 700, color: C.ink }}>{data.name}</p>
        <p style={{ margin: 0, fontSize: "11.5px", color: C.sub, marginBottom: "8px" }}>{data.gender} · {data.dob}</p>
        <p style={{ margin: "2px 0", fontSize: "11.5px", color: C.ink }}>{data.mobile}</p>
        <p style={{ margin: "2px 0", fontSize: "11.5px", color: C.ink }}>{data.email}</p>
      </div>

      <div style={{ padding: "10px 18px", background: "#f7f9fb", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <p style={{ margin: 0, fontSize: "10px", color: C.sub }}>Issued by National Health Authority</p>
        <span style={{ width: "22px", height: "22px", borderRadius: "6px", background: C.grad, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ShieldCheck size={12} color="#fff" />
        </span>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════════════ */
export default function AbhaRegistration() {
  const [patient, setPatient] = useState({
    name: "",
    mobile: "",
    gender: "Male",
    dob: "",
    email: "",
    address: "",
  });
  const [abhaInput, setAbhaInput] = useState("");
  const [mainStep, setMainStep] = useState(1);
  const [modal, setModal] = useState(null); // which modal is open
  const [searchBy, setSearchBy] = useState("abha");
  const [searchValue, setSearchValue] = useState("");
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(26);
  const [linked, setLinked] = useState(false);
  const [abhaData, setAbhaData] = useState(null);

  useEffect(() => {
    if ((modal === "verify-otp" || modal === "create-verify") && timer > 0) {
      const t = setTimeout(() => setTimer((s) => s - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [modal, timer]);

  function fmtTimer(s) {
    return `00:${String(s).padStart(2, "0")}`;
  }

  function openSearch() {
    setSearchValue("");
    setSearchBy("abha");
    setMainStep(2);
    setModal("search");
  }
  function openCreate() {
    setModal("create-generate");
  }

  function submitSearch() {
    setMainStep(3);
    setModal("generate-otp");
  }
  function goVerifyFromGenerate() {
    setTimer(26);
    setOtp("");
    setMainStep(4);
    setModal("verify-otp");
  }
  function verifyOtp() {
    setMainStep(5);
    setModal("details");
  }
  function linkAbha() {
    const data = {
      number: "97-1234-5678-9012",
      address: "kanaga@abdm",
      name: patient.name || "Kanagaraj",
      gender: patient.gender,
      dob: patient.dob || "01/01/1990",
      mobile: patient.mobile || "+91 98765 43210",
      email: patient.email || "kanagaraj@email.com",
    };
    setAbhaData(data);
    setMainStep(6);
    setModal("success");
  }
  function finishToRegistration() {
    setLinked(true);
    setModal(null);
  }

  /* Create-ABHA sub-flow */
  const [createStep, setCreateStep] = useState(1);
  function createGenerateOtp() {
    setTimer(26);
    setOtp("");
    setCreateStep(2);
    setModal("create-verify");
  }
  function createVerifyOtp() {
    setCreateStep(3);
    const data = {
      number: "97-1234-5678-9012",
      address: "kanaga@abdm",
      name: patient.name || "Kanagaraj",
      gender: patient.gender,
      dob: patient.dob || "01/01/1990",
      mobile: patient.mobile || "+91 98765 43210",
      email: patient.email || "kanagaraj@email.com",
    };
    setAbhaData(data);
    setMainStep(6);
    setModal("create-success");
  }
  function createFinish() {
    setLinked(true);
    setModal(null);
  }

  function resetAll() {
    setPatient({ name: "", mobile: "", gender: "Male", dob: "", email: "", address: "" });
    setAbhaInput("");
    setMainStep(1);
    setLinked(false);
    setAbhaData(null);
    setCreateStep(1);
    setModal(null);
  }

  return (
    <div style={{ background: C.page, minHeight: "100%", fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif" }}>
      <Navbar />
      <div style={{ padding: "20px", paddingTop: "84px" }}>
      <div style={{ maxWidth: "1180px", margin: "0 auto" }}>
        <Stepper current={mainStep} />

        <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: "20px", alignItems: "start" }}>
          {/* ── LEFT: Patient information form ── */}
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: "14px", padding: "22px 24px" }}>
            <p style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: C.ink, marginBottom: "14px" }}>Patient information</p>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "14px" }}>
              <Field label="Patient Name" required value={patient.name} onChange={(v) => setPatient({ ...patient, name: v })} placeholder="Enter patient name" />
              <Field label="Mobile Number" required value={patient.mobile} onChange={(v) => setPatient({ ...patient, mobile: v })} placeholder="+91" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "14px" }}>
              <div>
                <label style={labelStyle}>GENDER <span style={{ color: "#e0645f" }}>*</span></label>
                <select value={patient.gender} onChange={(e) => setPatient({ ...patient, gender: e.target.value })} style={inputStyle}>
                  <option>Male</option>
                  <option>Female</option>
                  <option>Other</option>
                </select>
              </div>
              <Field label="Date of Birth" required value={patient.dob} onChange={(v) => setPatient({ ...patient, dob: v })} placeholder="DD/MM/YYYY" />
            </div>
            <div style={{ marginBottom: "14px" }}>
              <Field label="Email ID" value={patient.email} onChange={(v) => setPatient({ ...patient, email: v })} placeholder="name@email.com" />
            </div>
            <div style={{ marginBottom: "18px" }}>
              <Field label="Address" value={patient.address} onChange={(v) => setPatient({ ...patient, address: v })} placeholder="Street, City, State" />
            </div>

            {/* ABHA info box */}
            <div style={{ border: `1px dashed ${C.border}`, borderRadius: "12px", padding: "16px", background: "#fbfcfd" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "7px", marginBottom: "12px" }}>
                <ShieldCheck size={15} color={C.teal} />
                <span style={{ fontSize: "13px", fontWeight: 700, color: C.ink }}>ABHA information</span>
              </div>
              <label style={labelStyle}>ABHA NUMBER / ABHA ADDRESS</label>
              <input
                value={abhaInput}
                onChange={(e) => setAbhaInput(e.target.value)}
                placeholder="Enter ABHA Number or ABHA Address"
                style={{ ...inputStyle, marginBottom: "12px" }}
              />
              <div style={{ display: "flex", gap: "10px" }}>
                <button onClick={openSearch} style={outlineBtn(C.teal)}>
                  <Search size={13} /> Search ABHA
                </button>
                <button onClick={openCreate} style={outlineBtn(C.orange)}>
                  <UserPlus size={13} /> Create ABHA
                </button>
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
              <button onClick={resetAll} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "11px 18px", borderRadius: "9px", border: `1px solid ${C.border}`, background: "#fff", color: C.ink, fontWeight: 600, fontSize: "13px", cursor: "pointer" }}>
                <RotateCcw size={14} /> Reset
              </button>
              <button style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "11px 18px", borderRadius: "9999px", border: "none", background: C.grad, color: "#fff", fontWeight: 600, fontSize: "13px", cursor: "pointer" }}>
                <FileCheck2 size={14} /> Save Patient
              </button>
            </div>
          </div>

          {/* ── RIGHT: ABHA card panel ── */}
          <div>
            {linked && abhaData ? (
              <AbhaCard data={abhaData} />
            ) : (
              <div
                style={{
                  border: `1.5px dashed ${C.border}`,
                  borderRadius: "14px",
                  padding: "48px 24px",
                  textAlign: "center",
                  background: C.card,
                }}
              >
                <Link2 size={26} color="#c3ccd6" style={{ marginBottom: "10px" }} />
                <p style={{ margin: 0, fontSize: "13.5px", fontWeight: 700, color: C.ink }}>ABHA card appears here</p>
                <p style={{ margin: "4px 0 0", fontSize: "11.5px", color: C.sub, lineHeight: 1.5 }}>
                  Link or create an ABHA to generate the patient's digital health card
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ══════════════ MODALS — Search ABHA flow (teal) ══════════════ */}
      {modal === "search" && (
        <Modal onClose={() => setModal(null)}>
          <ModalHeader badge="1" badgeBg={C.grad} badgeColor="#fff" title="ABHA Search" subtitle="Search for existing ABHA" onClose={() => setModal(null)} />
          <p style={{ fontSize: "11px", fontWeight: 700, color: C.sub, marginBottom: "8px", letterSpacing: "0.03em" }}>SEARCH BY</p>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "14px" }}>
            {[
              ["abha", "ABHA Number"],
              ["mobile", "Mobile Number"],
              ["address", "ABHA Address"],
            ].map(([val, label]) => (
              <label key={val} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: C.ink, cursor: "pointer" }}>
                <input type="radio" checked={searchBy === val} onChange={() => setSearchBy(val)} />
                {label}
              </label>
            ))}
          </div>
          <label style={labelStyle}>ENTER VALUE</label>
          <input
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="97-1234-5678-9012"
            style={{ ...inputStyle, marginBottom: "14px" }}
          />
          <div style={{ background: "#eafcf8", border: "1px solid #bdeee4", borderRadius: "9px", padding: "10px 12px" }}>
            <p style={{ margin: 0, fontSize: "11.5px", fontWeight: 700, color: C.teal, marginBottom: "4px" }}>📍 Tips</p>
            <ul style={{ margin: 0, paddingLeft: "16px", fontSize: "11px", color: "#4d6478", lineHeight: 1.7 }}>
              <li>Enter valid 14 digit ABHA number</li>
              <li>Example: 97-1234-5678-9012</li>
              <li>Ensure correct mobile is registered with ABHA</li>
            </ul>
          </div>
          <ModalButtons
            leftLabel="Close"
            onLeft={() => setModal(null)}
            rightLabel="Search"
            rightIcon={<Search size={13} />}
            rightBg={C.grad}
            onRight={submitSearch}
          />
        </Modal>
      )}

      {modal === "generate-otp" && (
        <Modal onClose={() => setModal(null)}>
          <ModalHeader badge="2" badgeBg={C.grad} badgeColor="#fff" title="Generate OTP" subtitle="OTP will be sent to registered mobile number" onClose={() => setModal(null)} />
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "10px 0 6px" }}>
            <span style={{ width: "56px", height: "56px", borderRadius: "14px", background: "#eef2f6", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "14px" }}>
              <MessageSquare size={24} color={C.teal} />
            </span>
            <p style={{ margin: 0, fontSize: "12.5px", color: C.sub }}>OTP has been sent successfully to</p>
            <p style={{ margin: "2px 0 12px", fontSize: "15px", fontWeight: 700, color: C.ink }}>{patient.mobile || "+91 98765 43210"}</p>
            <span style={{ background: "#eafcf2", color: "#0f8a4c", fontSize: "11px", fontWeight: 700, borderRadius: "9999px", padding: "5px 12px" }}>
              ⏱ OTP will expire in 5 minutes
            </span>
          </div>
          <ModalButtons leftLabel="Close" onLeft={() => setModal(null)} rightLabel="Next →" rightBg={C.grad} onRight={goVerifyFromGenerate} />
        </Modal>
      )}

      {modal === "verify-otp" && (
        <Modal onClose={() => setModal(null)}>
          <ModalHeader badge="3" badgeBg={C.grad} badgeColor="#fff" title="Verify OTP" subtitle="Enter the OTP sent to your mobile number" onClose={() => setModal(null)} />
          <div style={{ textAlign: "center", marginBottom: "6px" }}>
            <p style={{ margin: 0, fontSize: "13px", fontWeight: 700, color: C.ink }}>Enter 6 digit OTP</p>
            <p style={{ margin: "2px 0 16px", fontSize: "11.5px", color: C.sub }}>
              We have sent a 6 digit OTP to <strong style={{ color: C.ink }}>{patient.mobile || "+91 98765 43210"}</strong>
            </p>
          </div>
          <OtpBoxes value={otp} onChange={setOtp} accent={C.teal} />
          <p style={{ textAlign: "center", fontSize: "11.5px", color: C.sub, marginTop: "12px" }}>
            {timer > 0 ? `Resend OTP in ${fmtTimer(timer)}` : <button onClick={() => setTimer(26)} style={{ border: "none", background: "none", color: C.teal, fontWeight: 700, cursor: "pointer" }}>Resend OTP</button>}
          </p>
          <ModalButtons
            leftLabel="Cancel"
            onLeft={() => setModal(null)}
            rightLabel="Verify OTP"
            rightBg={C.grad}
            rightDisabled={otp.length < 6}
            onRight={verifyOtp}
          />
        </Modal>
      )}

      {modal === "details" && (
        <Modal onClose={() => setModal(null)}>
          <ModalHeader badge="4" badgeBg={C.grad} badgeColor="#fff" title="ABHA Details" subtitle="Verify ABHA details" onClose={() => setModal(null)} />
          <p style={{ fontSize: "11px", fontWeight: 700, color: C.sub, marginBottom: "8px", letterSpacing: "0.03em" }}>ABHA PROFILE</p>
          <div style={{ display: "flex", gap: "10px", marginBottom: "12px" }}>
            <span style={{ width: "36px", height: "36px", borderRadius: "9999px", background: "#eef2f6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <UserPlus size={16} color={C.sub} />
            </span>
            <div style={{ fontSize: "12px", color: C.sub, lineHeight: 1.8 }}>
              <p style={{ margin: 0 }}>Name <b style={{ color: C.ink, marginLeft: "6px" }}>{patient.name || "Kanagaraj"}</b></p>
              <p style={{ margin: 0 }}>Gender <b style={{ color: C.ink, marginLeft: "6px" }}>{patient.gender}</b></p>
              <p style={{ margin: 0 }}>Date of Birth <b style={{ color: C.ink, marginLeft: "6px" }}>{patient.dob || "01/01/1990"}</b></p>
            </div>
          </div>
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: "10px", fontSize: "12px", color: C.sub, lineHeight: 2 }}>
            <p style={{ margin: 0 }}>ABHA Number <b style={{ color: C.ink, float: "right" }}>97-1234-5678-9012</b></p>
            <p style={{ margin: 0 }}>ABHA Address <b style={{ color: C.ink, float: "right" }}>kanaga@abdm</b></p>
            <p style={{ margin: 0 }}>Mobile Number <b style={{ color: C.ink, float: "right" }}>{patient.mobile || "+91 98765 43210"}</b></p>
            <p style={{ margin: 0 }}>Email ID <b style={{ color: C.ink, float: "right" }}>{patient.email || "kanagaraj@email.com"}</b></p>
          </div>
          <div style={{ background: "#fff8e6", border: "1px solid #f5deab", borderRadius: "8px", padding: "9px 12px", marginTop: "14px", fontSize: "11.5px", color: "#9a6b0a" }}>
            ⚠ Please verify the above details before linking ABHA.
          </div>
          <ModalButtons leftLabel="Cancel" onLeft={() => setModal(null)} rightLabel="Link ABHA" rightIcon={<Link2 size={13} />} rightBg={C.grad} onRight={linkAbha} />
        </Modal>
      )}

      {modal === "success" && (
        <Modal onClose={() => setModal(null)}>
          <ModalHeader badge={<Check size={13} strokeWidth={3} />} badgeBg={C.green} badgeColor="#fff" title="Link Success" subtitle="ABHA linked successfully" onClose={() => setModal(null)} />
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <p style={{ fontSize: "22px", margin: "0 0 6px" }}>🎉 ✨ 🎉</p>
            <span style={{ width: "56px", height: "56px", borderRadius: "9999px", background: C.greenBg, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: "12px" }}>
              <Check size={26} color={C.green} strokeWidth={3} />
            </span>
            <p style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: C.green }}>ABHA linked successfully!</p>
            <p style={{ margin: "3px 0 14px", fontSize: "12px", color: C.sub }}>ABHA has been successfully linked with the patient.</p>
            <div style={{ background: C.greenBg, border: "1px solid #bdeccf", borderRadius: "9px", padding: "10px", textAlign: "left" }}>
              <p style={{ margin: 0, fontSize: "10px", fontWeight: 700, color: "#0f8a4c" }}>ABHA NUMBER</p>
              <p style={{ margin: 0, fontSize: "14px", fontWeight: 700, color: C.ink }}>97-1234-5678-9012</p>
            </div>
          </div>
          <button
            onClick={finishToRegistration}
            style={{ width: "100%", marginTop: "14px", padding: "11px 0", borderRadius: "9999px", border: "none", background: C.grad, color: "#fff", fontWeight: 700, fontSize: "13px", cursor: "pointer" }}
          >
            Continue to Registration
          </button>
        </Modal>
      )}

      {/* ══════════════ MODALS — Create ABHA flow (blue) ══════════════ */}
      {(modal === "create-generate" || modal === "create-verify" || modal === "create-success") && (
        <Modal onClose={() => setModal(null)}>
          <ModalHeader badge="C" badgeBg={C.grad} badgeColor="#fff" title="Create ABHA" onClose={() => setModal(null)} />
          {/* sub-stepper */}
          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "18px", marginTop: "-8px" }}>
            {["Generate OTP", "Verify OTP", "ABHA Created"].map((label, i) => {
              const idx = i + 1;
              const done = idx < createStep;
              const active = idx === createStep;
              return (
                <div key={label} style={{ display: "flex", alignItems: "center", flex: i < 2 ? 1 : "0 0 auto" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11px", fontWeight: 700, color: done ? "#8a8f96" : active ? C.orange : "#b7bfc9", whiteSpace: "nowrap" }}>
                    <span style={{ width: "16px", height: "16px", borderRadius: "9999px", background: done ? "#e4e7eb" : active ? C.grad : "#eef1f4", color: done ? "#8a8f96" : active ? "#fff" : "#b7bfc9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "9px" }}>
                      {done ? <Check size={9} strokeWidth={3} /> : idx}
                    </span>
                    {label}
                  </span>
                  {i < 2 && <div style={{ flex: 1, height: "1.5px", background: idx < createStep ? "#d7dbe0" : "#e4e9ee", margin: "0 8px" }} />}
                </div>
              );
            })}
          </div>

          {modal === "create-generate" && (
            <>
              <div style={{ textAlign: "center", padding: "6px 0 4px" }}>
                <span style={{ width: "64px", height: "64px", borderRadius: "9999px", background: C.orangeBg, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: "14px", position: "relative" }}>
                  <UserPlus size={26} color={C.orange} />
                </span>
                <p style={{ margin: "0 auto", fontSize: "12.5px", color: C.sub, maxWidth: "260px", lineHeight: 1.6 }}>
                  Click below to generate OTP and create new ABHA for this patient.
                </p>
              </div>
              <ModalButtons leftLabel="Close" onLeft={() => setModal(null)} rightLabel="Generate OTP" rightBg={C.grad} onRight={createGenerateOtp} />
            </>
          )}

          {modal === "create-verify" && (
            <>
              <div style={{ textAlign: "center", marginBottom: "6px" }}>
                <p style={{ margin: 0, fontSize: "13px", fontWeight: 700, color: C.ink }}>Enter 6 digit OTP</p>
                <p style={{ margin: "2px 0 16px", fontSize: "11.5px", color: C.sub }}>
                  We have sent a 6 digit OTP to <strong style={{ color: C.ink }}>{patient.mobile || "+91 98765 43210"}</strong>
                </p>
              </div>
              <OtpBoxes value={otp} onChange={setOtp} accent={C.orange} />
              <p style={{ textAlign: "center", fontSize: "11.5px", color: C.sub, marginTop: "12px" }}>
                {timer > 0 ? `Resend OTP in ${fmtTimer(timer)}` : <button onClick={() => setTimer(26)} style={{ border: "none", background: "none", color: C.orange, fontWeight: 700, cursor: "pointer" }}>Resend OTP</button>}
              </p>
              <ModalButtons leftLabel="Cancel" onLeft={() => setModal(null)} rightLabel="Verify OTP" rightBg={C.grad} rightDisabled={otp.length < 6} onRight={createVerifyOtp} />
            </>
          )}

          {modal === "create-success" && (
            <>
              <div style={{ textAlign: "center", padding: "4px 0" }}>
                <p style={{ fontSize: "22px", margin: "0 0 6px" }}>🎉 ✨ 🎉</p>
                <span style={{ width: "56px", height: "56px", borderRadius: "9999px", background: C.greenBg, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: "12px" }}>
                  <Check size={26} color={C.green} strokeWidth={3} />
                </span>
                <p style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: C.green }}>ABHA created successfully!</p>
                <p style={{ margin: "3px 0 14px", fontSize: "12px", color: C.sub, maxWidth: "280px", marginLeft: "auto", marginRight: "auto" }}>
                  Your new ABHA has been created. After creation, ABHA will be auto linked with the patient.
                </p>
                <div style={{ background: C.greenBg, border: "1px solid #bdeccf", borderRadius: "9px", padding: "10px", textAlign: "left" }}>
                  <p style={{ margin: 0, fontSize: "10px", fontWeight: 700, color: "#0f8a4c" }}>ABHA NUMBER</p>
                  <p style={{ margin: 0, fontSize: "14px", fontWeight: 700, color: C.ink }}>97-1234-5678-9012</p>
                </div>
              </div>
              <button
                onClick={createFinish}
                style={{ width: "100%", marginTop: "14px", padding: "11px 0", borderRadius: "9999px", border: "none", background: C.grad, color: "#fff", fontWeight: 700, fontSize: "13px", cursor: "pointer" }}
              >
                Continue
              </button>
            </>
          )}
        </Modal>
      )}

      <style>{`
        @keyframes abhaPop {
          from { transform: scale(0.94); opacity: 0; }
          to   { transform: scale(1); opacity: 1; }
        }
      `}</style>
      </div>
    </div>
  );
}

/* ── small shared bits ───────────────────────────────────────────────── */
const labelStyle = { display: "block", fontSize: "10.5px", fontWeight: 700, color: C.sub, letterSpacing: "0.03em", marginBottom: "6px" };
const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: "8px",
  border: `1px solid ${C.border}`,
  background: C.fieldBg,
  fontSize: "13px",
  color: C.ink,
  outline: "none",
  boxSizing: "border-box",
};

function Field({ label, required, value, onChange, placeholder }) {
  return (
    <div>
      <label style={labelStyle}>
        {label.toUpperCase()} {required && <span style={{ color: "#e0645f" }}>*</span>}
      </label>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={inputStyle} />
    </div>
  );
}

function outlineBtn(color) {
  return {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    padding: "9px 0",
    borderRadius: "9999px",
    border: `1.5px solid ${color}`,
    background: "#fff",
    color,
    fontWeight: 700,
    fontSize: "12.5px",
    cursor: "pointer",
  };
}